import {
  AssetClassesQuery,
  EditInvestorPageGroupByPrimaryEntityIdQuery,
  EditInvestorPageLegalEntityQuery,
  InvestorDetailsGetInvestorLegalEntitiesQuery,
  PaginateSortDirection,
  Role
} from '@/__generated__/graphql';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { SectionZeroState } from '@/components/basicComponents';
import { Accordion } from '@/components/basicComponents/accordion';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { Loader } from '@/components/loaders/loader';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { GET_ASSET_CLASSES_QUERY } from '@/components/settings-page/assetClassDefaultTargets/queries';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { useStatusData } from '@/hooks/useStatusData';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { INVESTOR_LEGAL_ENTITIES_QUERY } from '../investorDetailsPage/queries';
import { ChartWithTable } from '../investorDetailsPage/tabs/pacingTab/modalPages/autoPlanWizardModalPage/steps/allocationTargets/chartWithTable';
import { InvestorDetailsEntityTypeEnum } from '../investorDetailsPage/types';
import DeleteEntity from './components/modals/deleteEntity';
import MoveEntity from './components/modals/moveEntity';
import { editInvestorSections } from './constants';
import {
  DataIntegrationModalPage,
  EditAllocationSettingsModalPage,
  EditAllocationTargetsModalPage,
  EditEntityDetailsModalPage,
  EditHouseholdModalPage,
  EntityGroupingModalPage
} from './modalPages';
import {
  EDIT_INVESTOR_PAGE_DEFAULT_DRAWDOWN_TARGETS_QUERY,
  EDIT_INVESTOR_PAGE_GROUP_BY_PRIMARY_ENTITY_ID_QUERY,
  EDIT_INVESTOR_PAGE_HOUSEHOLD_SECTION_QUERY,
  EDIT_INVESTOR_PAGE_LEGAL_ENTITY_QUERY
} from './queries';
import {
  EditInvestorModalPageWrapper,
  EditInvestorPreviewFieldItem,
  EditInvestorPreviewFields,
  EditInvestorPreviewFieldTitle,
  EditInvestorPreviewFieldValue,
  EditInvestorSectionsWrapper
} from './styles';
import {
  EditInvestorAllocationTargetsCapacityCalculationOptionEnum,
  EditInvestorEntityDetailsFormMode,
  EditInvestorModalPageTypeEnum,
  IEditInvestorFormData,
  IEditInvestorSectionsData
} from './types';
import { buildEditInvestorSectionsData } from './utils';

const shouldShowChartWithTable = (section: IEditInvestorSectionsData, entityType: InvestorDetailsEntityTypeEnum) => {
  const isAllocationTargetsSection = section.name === 'allocationTargets';
  const isCapacityCalculationEnabled =
    'capacityCalculationOption' in section.formData &&
    section.formData.capacityCalculationOption !== EditInvestorAllocationTargetsCapacityCalculationOptionEnum.DisregardAllocationTargets;
  const isNotGroupedEntity = entityType !== InvestorDetailsEntityTypeEnum.GroupedEntity;

  return isAllocationTargetsSection && isCapacityCalculationEnabled && isNotGroupedEntity;
};

export const EditInvestor = () => {
  const goBack = useGoBack();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { data: statusData } = useStatusData();
  const { searchParams, setSearchParams } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';
  const backToTitle = searchParams.get('backToTitle') || '';
  const modalPageType: EditInvestorModalPageTypeEnum = (searchParams.get('modalPageType') as EditInvestorModalPageTypeEnum) || null;
  const isGroup = searchParams.get('isGroup') === 'true';

  const [entityType, setEntityType] = useState<InvestorDetailsEntityTypeEnum>(InvestorDetailsEntityTypeEnum.Household);
  const [currentFormData, setCurrentFormData] = useState<IEditInvestorFormData[keyof IEditInvestorFormData] | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [entitiesSelectOptions, setEntitiesSelectOptions] = useState<ISelectValue[]>([]);
  const [editInvestorHouseholdData, setEditInvestorHouseholdData] = useState<IEditInvestorFormData['household']>();
  const [familyTenantId, setFamilyTenantId] = useState<string | null>(null);
  const [editInvestorSectionsData, setEditInvestorSectionsData] = useState<IEditInvestorSectionsData[]>([]);
  const [assetClasses, setAssetClasses] = useState<AssetClassesQuery['assetClasses']>([]);
  const [drawdownTargets, setDrawdownTargets] = useState<EditInvestorPageLegalEntityQuery['legalEntity']['drawdownTargets'] | null>(null);
  const [groups, setGroups] = useState<InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities']>([]);
  const [selectedGroupName, setSelectedGroupName] = useState<string>('');
  const [showLoaderOnWholeScreen, setShowLoaderOnWholeScreen] = useState(true);
  const [buildSectionsLoading, setBuildSectionsLoading] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  const [getAssetClasses] = useLazyQuery(GET_ASSET_CLASSES_QUERY);
  const [getDefaultDrawdownTargets] = useLazyQuery(EDIT_INVESTOR_PAGE_DEFAULT_DRAWDOWN_TARGETS_QUERY);

  useEffect(() => {
    if (location?.pathname?.includes('create-investor')) {
      handleModalPage({ type: EditInvestorModalPageTypeEnum.AddInvestorEntity });
      setShowLoaderOnWholeScreen(false);
    }
  }, [location?.pathname]);

  useEffect(() => {
    if (!modalPageType || !!currentFormData) return;

    const section = editInvestorSectionsData?.find((section) => section.modalPageType === modalPageType);
    if (section) {
      setCurrentFormData(section.formData);
    }
  }, [modalPageType, editInvestorSectionsData]);

  const {
    data: investorLegalEntitiesData,
    loading: investorLegalEntitiesLoading,
    refetch: investorLegalEntitiesRefetch
  } = useQuery(INVESTOR_LEGAL_ENTITIES_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !investorId,
    variables: {
      data: {
        id: investorId,
        legalEntitiesList: {
          sortBy: 'name',
          sortDirection: PaginateSortDirection.Asc
        }
      }
    },
    onCompleted: (data) => {
      const { legalEntities } = data.getInvestor;
      const selectOptions = legalEntities.flatMap((entity) => {
        if (entity.isGroup && entity.entities) {
          const groupedEntities = entity.entities.map((groupedEntity) => createSimpleSelectOption(groupedEntity.id, `- ${groupedEntity.entityName}`));
          return [createSimpleSelectOption(`group-${entity.id}`, entity.entityName), ...groupedEntities];
        }
        return [createSimpleSelectOption(entity.id, entity.entityName)];
      });
      const groups = legalEntities.filter((entity) => entity?.isGroup);

      setGroups(groups || []);
      setEntitiesSelectOptions(selectOptions);
    },
    onError: (error) => {
      console.error(error);
      navigate('/404', { replace: true });
    }
  });

  const { refetch: householdSectionDataRefetch } = useQuery(EDIT_INVESTOR_PAGE_HOUSEHOLD_SECTION_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !investorId,
    variables: {
      data: {
        id: investorId
      }
    },
    onCompleted: (data) => {
      const { name, advisor, tenant } = data.getInvestor;
      const hasAdvisorRole =
        advisor?.roles?.includes(Role.AdvisorAssociate) || advisor?.roles?.includes(Role.AdvisorLead) || advisor?.roles?.includes(Role.AdvisorMember);
      setEditInvestorHouseholdData({
        name,
        tenantId: tenant?.id || '',
        tenantName: tenant?.name || '',
        advisorId: hasAdvisorRole && advisor?.id ? advisor.id : '',
        advisorName: hasAdvisorRole && advisor?.firstName && advisor?.lastName ? `${advisor.firstName} ${advisor.lastName}` : '',
        clientAdvisorOption: hasAdvisorRole && advisor?.id ? 'Assign a client advisor' : 'None'
      });

      setFamilyTenantId(tenant?.id || null);

      if (!entityId) {
        setShowLoaderOnWholeScreen(false);
      }
    },
    onError: (error) => {
      console.error(error);
      if (!entityId) {
        setShowLoaderOnWholeScreen(false);
      }
    }
  });

  const {
    data: groupData,
    loading: groupDataLoading,
    networkStatus: groupDataNetworkStatus,
    refetch: groupDataRefetch
  } = useQuery(EDIT_INVESTOR_PAGE_GROUP_BY_PRIMARY_ENTITY_ID_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !entityId || !entityType || entityType !== InvestorDetailsEntityTypeEnum.Group,
    variables: {
      data: {
        primaryEntityId: entityId
      }
    },
    onError: (error) => {
      console.error(error);
      setShowLoaderOnWholeScreen(false);
      setEditInvestorSectionsData([]);
    }
  });

  const {
    data: legalEntityData,
    loading: legalEntityDataLoading,
    networkStatus: legalEntityDataNetworkStatus,
    refetch: legalEntityDataRefetch
  } = useQuery(EDIT_INVESTOR_PAGE_LEGAL_ENTITY_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !entityId || !entityType || entityType === InvestorDetailsEntityTypeEnum.Group || entityType === InvestorDetailsEntityTypeEnum.Household,
    variables: {
      id: entityId
    },
    onError: (error) => {
      console.error(error);
      setShowLoaderOnWholeScreen(false);
      setEditInvestorSectionsData([]);
    }
  });

  const fetchDataAndBuildSections = async ({
    legalEntityData,
    groupData
  }: {
    legalEntityData?: EditInvestorPageLegalEntityQuery['legalEntity'];
    groupData?: EditInvestorPageGroupByPrimaryEntityIdQuery['group'];
  }) => {
    try {
      setBuildSectionsLoading(true);

      const { tenant, allocationTargets, drawdownTargets } = legalEntityData || groupData || {};

      const { data: assetClassesData } = await getAssetClasses({
        variables: {
          tenantId: tenant?.id
        }
      });
      const sectionsData = buildEditInvestorSectionsData({
        legalEntityData,
        groupData,
        assetClasses: assetClassesData?.assetClasses,
        targetSubAssetClasses: statusData?.target_sub_asset_classes,
        entityType
      });
      setEditInvestorSectionsData(sectionsData);
      setAssetClasses(assetClassesData?.assetClasses || []);

      if (!!allocationTargets?.length) {
        setDrawdownTargets(drawdownTargets || null);
        return;
      }

      const { data: defaultDrawdownTargetsData } = await getDefaultDrawdownTargets({
        variables: {
          tenantId: tenant?.id
        }
      });
      setDrawdownTargets(defaultDrawdownTargetsData?.defaultDrawdownTargets || null);
    } catch (error) {
      console.error(error);
    } finally {
      setBuildSectionsLoading(false);
      setShowLoaderOnWholeScreen(false);
    }
  };

  useEffect(() => {
    if (groupDataLoading || legalEntityDataLoading) return;

    if (legalEntityData?.legalEntity) {
      fetchDataAndBuildSections({ legalEntityData: legalEntityData.legalEntity });
      return;
    }
    if (groupData?.group) {
      fetchDataAndBuildSections({ groupData: groupData.group });
      return;
    }
  }, [groupDataLoading, groupData, legalEntityDataLoading, legalEntityData]);

  const resetEntityType = () => {
    setEntityType(null);
  };

  const handleEntitySelect = (entityId: string, { shouldResetEntityType = false } = {}) => {
    if (shouldResetEntityType) {
      resetEntityType();
    }
    let entityIdToNavigate = entityId;
    if (entityId?.includes('group-')) {
      entityIdToNavigate = entityId?.split('group-')?.[1];
    }
    const queryParams = new URLSearchParams({
      backToTitle,
      ...(entityIdToNavigate && { isGroup: entityId?.includes('group-') ? 'true' : 'false' })
    });
    if (entityIdToNavigate) {
      navigate(`/investors/${investorId}/${entityIdToNavigate}/edit?${queryParams.toString()}`, { replace: true });
      return;
    }
    navigate(`/investors/${investorId}/edit?${queryParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    if (investorLegalEntitiesLoading || !investorLegalEntitiesData) return;

    if (!entityId) {
      if (investorLegalEntitiesData.getInvestor.legalEntities?.length === 1 && !investorLegalEntitiesData.getInvestor.legalEntities?.[0]?.isGroup) {
        const entityId = investorLegalEntitiesData.getInvestor.legalEntities?.[0]?.id;
        handleEntitySelect(entityId);
        setEntityType(InvestorDetailsEntityTypeEnum.UngroupedEntity);
        return;
      }
      setEntityType(InvestorDetailsEntityTypeEnum.Household);
      return;
    }

    const topLevelEntity = investorLegalEntitiesData.getInvestor.legalEntities.find((entity) => entity.id === entityId);

    if (topLevelEntity?.isGroup && isGroup) {
      setEntityType(InvestorDetailsEntityTypeEnum.Group);
      return;
    }

    const groupEntity = investorLegalEntitiesData.getInvestor.legalEntities.find(
      (entity) => entity.entities?.some((groupedEntity) => groupedEntity.id === entityId)
    );

    if (groupEntity) {
      setSelectedGroupName(groupEntity.entityName);
      setEntityType(InvestorDetailsEntityTypeEnum.GroupedEntity);
      return;
    }

    if (topLevelEntity) {
      setEntityType(InvestorDetailsEntityTypeEnum.UngroupedEntity);
      return;
    }

    setEntityType(InvestorDetailsEntityTypeEnum.Household);
    setShowLoaderOnWholeScreen(false);
  }, [entityId, isGroup, investorLegalEntitiesLoading, investorLegalEntitiesData]);

  const handleScrollTo = (top: number) => {
    setTimeout(() => {
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }, 0);
  };

  const handleModalPage = ({
    type,
    formData,
    updateSearchParams = true
  }: {
    type: EditInvestorModalPageTypeEnum | null;
    formData?: IEditInvestorFormData[keyof IEditInvestorFormData];
    updateSearchParams?: boolean;
  }) => {
    if (updateSearchParams) {
      setSearchParams(
        (prev) => {
          if (type) {
            prev.set('modalPageType', type);
          } else {
            prev.delete('modalPageType');
          }
          return prev;
        },
        { replace: true }
      );
    }
    setCurrentFormData(formData || null);
    setScrollPosition(!!type ? window.scrollY : 0);
    handleScrollTo(!!type ? 0 : scrollPosition);
  };

  const handleModalWindow = ({ type }: { type: string }) => {
    setModalWindow({ type, isOpen: !!type });
  };

  const refetchEntityOrGroupData = async () => {
    if (entityType === InvestorDetailsEntityTypeEnum.Group) {
      await groupDataRefetch();
      return;
    }
    await legalEntityDataRefetch();
  };

  if (showLoaderOnWholeScreen) {
    return <LoaderOnWholeScreen />;
  }

  if (!!modalPageType) {
    return (
      <>
        {modalPageType === EditInvestorModalPageTypeEnum.Household && (
          <EditHouseholdModalPage
            householdFormData={editInvestorHouseholdData}
            householdSectionDataRefetch={householdSectionDataRefetch}
            handleCloseModalPage={() => handleModalPage({ type: null })}
          />
        )}
        {modalPageType === EditInvestorModalPageTypeEnum.AddInvestorEntity && (
          <EditEntityDetailsModalPage
            formMode={EditInvestorEntityDetailsFormMode.Create}
            entityDetailsFormData={null}
            familyTenantId={familyTenantId}
            refetchEntityOrGroupData={refetchEntityOrGroupData}
            investorLegalEntitiesRefetch={investorLegalEntitiesRefetch}
            handleCloseModalPage={({ updateSearchParams = true }) => handleModalPage({ type: null, updateSearchParams })}
          />
        )}
        {modalPageType === EditInvestorModalPageTypeEnum.EntityDetails && (
          <EditEntityDetailsModalPage
            formMode={EditInvestorEntityDetailsFormMode.Update}
            entityDetailsFormData={currentFormData as IEditInvestorFormData['entityDetails']}
            familyTenantId={familyTenantId}
            refetchEntityOrGroupData={refetchEntityOrGroupData}
            investorLegalEntitiesRefetch={investorLegalEntitiesRefetch}
            handleCloseModalPage={({ updateSearchParams }) => handleModalPage({ type: null, updateSearchParams })}
          />
        )}
        {modalPageType === EditInvestorModalPageTypeEnum.AllocationTargets && (
          <EditAllocationTargetsModalPage
            entityType={entityType}
            selectedGroupName={selectedGroupName}
            assetClasses={assetClasses}
            allocationTargetsFormData={currentFormData as IEditInvestorFormData['allocationTargets']}
            refetchEntityOrGroupData={refetchEntityOrGroupData}
            handleCloseModalPage={() => handleModalPage({ type: null })}
          />
        )}
        {modalPageType === EditInvestorModalPageTypeEnum.AllocationSettings && (
          <EditAllocationSettingsModalPage
            allocationSettingsFormData={currentFormData as IEditInvestorFormData['allocationSettings']}
            refetchEntityOrGroupData={refetchEntityOrGroupData}
            handleCloseModalPage={() => handleModalPage({ type: null })}
          />
        )}
        {modalPageType === EditInvestorModalPageTypeEnum.DataIntegration && (
          <DataIntegrationModalPage
            dataIntegrationFormData={currentFormData as IEditInvestorFormData['dataIntegration']}
            refetchEntityOrGroupData={refetchEntityOrGroupData}
            handleCloseModalPage={() => handleModalPage({ type: null })}
          />
        )}
        {modalPageType === EditInvestorModalPageTypeEnum.EntityGrouping && (
          <EntityGroupingModalPage
            groups={groups}
            investorLegalEntities={investorLegalEntitiesData?.getInvestor?.legalEntities || []}
            investorLegalEntitiesRefetch={investorLegalEntitiesRefetch}
            handleCloseModalPage={() => handleModalPage({ type: null })}
          />
        )}
      </>
    );
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'delete-entity' && (
        <DeleteEntity
          isOpen={modalWindow.isOpen}
          entityName={legalEntityData?.legalEntity?.entityName}
          entitiesSelectOptions={entitiesSelectOptions}
          investorLegalEntitiesRefetch={investorLegalEntitiesRefetch}
          onClose={() => handleModalWindow({ type: '' })}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'move-entity' && (
        <MoveEntity
          isOpen={modalWindow.isOpen}
          entityName={legalEntityData?.legalEntity?.entityName}
          isLastEntity={entitiesSelectOptions?.length === 1}
          editInvestorHouseholdData={editInvestorHouseholdData}
          onClose={() => handleModalWindow({ type: '' })}
        />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/investors' })} backToTitle={backToTitle} />} />
        <PageTitle title="Edit Investor" />
      </MainWrap>
      <MainWrap>
        <EditInvestorModalPageWrapper>
          <EditInvestorSectionsWrapper>
            <Accordion title="Household" onEdit={() => handleModalPage({ type: EditInvestorModalPageTypeEnum.Household })}>
              <EditInvestorPreviewFields>
                {!!editInvestorHouseholdData?.name && (
                  <EditInvestorPreviewFieldItem isMobile={isMobile}>
                    <EditInvestorPreviewFieldTitle>Investor Household</EditInvestorPreviewFieldTitle>
                    <EditInvestorPreviewFieldValue>{editInvestorHouseholdData.name}</EditInvestorPreviewFieldValue>
                  </EditInvestorPreviewFieldItem>
                )}
                {!!editInvestorHouseholdData?.advisorName && (
                  <EditInvestorPreviewFieldItem isMobile={isMobile}>
                    <EditInvestorPreviewFieldTitle>Advisor</EditInvestorPreviewFieldTitle>
                    <EditInvestorPreviewFieldValue>{editInvestorHouseholdData.advisorName}</EditInvestorPreviewFieldValue>
                  </EditInvestorPreviewFieldItem>
                )}
              </EditInvestorPreviewFields>
            </Accordion>
            <SelectEntitySection isMobile={isMobile}>
              <EntitySelectWrapper>
                {entitiesSelectOptions?.length === 1 ? (
                  <EllipsisWithTooltip title={entitiesSelectOptions[0].label} maxContentWidth={300}>
                    <EntityLabel>{entitiesSelectOptions[0].label}</EntityLabel>
                  </EllipsisWithTooltip>
                ) : (
                  <>
                    {entityType === InvestorDetailsEntityTypeEnum.GroupedEntity && <EntityLabel isGroupedEntity>{selectedGroupName}</EntityLabel>}
                    <EntitySelect
                      value={entityType === InvestorDetailsEntityTypeEnum.Group ? `group-${entityId}` : entityId}
                      setValue={(entityId) => handleEntitySelect(entityId, { shouldResetEntityType: true })}
                      options={[{ label: 'Select An Entity', value: '' }, ...entitiesSelectOptions]}
                      loading={investorLegalEntitiesLoading}
                      width="100%"
                      additionalOption={
                        <AddNewEntityButton
                          styleType="outline"
                          size="md"
                          onClick={() => handleModalPage({ type: EditInvestorModalPageTypeEnum.AddInvestorEntity })}
                        >
                          Add New Entity
                        </AddNewEntityButton>
                      }
                    />
                  </>
                )}
              </EntitySelectWrapper>
              <SelectEntitySectionButtons isMobile={isMobile}>
                {entitiesSelectOptions?.length > 1 && (
                  <Button styleType="outline" size="lg" onClick={() => handleModalPage({ type: EditInvestorModalPageTypeEnum.EntityGrouping })}>
                    Edit Grouping
                  </Button>
                )}
                {entityType === InvestorDetailsEntityTypeEnum.UngroupedEntity && (
                  <>
                    <Button styleType="outline" size="lg" onClick={() => handleModalWindow({ type: 'move-entity' })}>
                      Move
                    </Button>
                    <Button styleType="outline" size="lg" onClick={() => handleModalWindow({ type: 'delete-entity' })}>
                      Delete
                    </Button>
                    {entitiesSelectOptions?.length === 1 && (
                      <Button
                        styleType="outline"
                        size="lg"
                        onClick={() => handleModalPage({ type: EditInvestorModalPageTypeEnum.AddInvestorEntity })}
                      >
                        Add New Entity
                      </Button>
                    )}
                  </>
                )}
              </SelectEntitySectionButtons>
            </SelectEntitySection>
            {buildSectionsLoading ||
            groupDataNetworkStatus === NetworkStatus.loading ||
            groupDataNetworkStatus === NetworkStatus.setVariables ||
            legalEntityDataNetworkStatus === NetworkStatus.loading ||
            legalEntityDataNetworkStatus === NetworkStatus.setVariables ? (
              <Loader />
            ) : (
              <>
                {entityType !== InvestorDetailsEntityTypeEnum.Household && (
                  <>
                    {editInvestorSectionsData.map(
                      (section) =>
                        editInvestorSections[entityType]?.includes(section.name) && (
                          <Accordion
                            key={section.name}
                            id={section.modalPageType}
                            title={section.title}
                            onEdit={() => handleModalPage({ type: section.modalPageType, formData: section.formData })}
                          >
                            {section?.isEmpty ? (
                              <SectionZeroState
                                button={{
                                  name: 'Add Information',
                                  onClick: () => handleModalPage({ type: section.modalPageType, formData: section.formData })
                                }}
                              />
                            ) : (
                              <>
                                <EditInvestorPreviewFields>
                                  {section.displayData.map(
                                    (item) =>
                                      item.value && (
                                        <EditInvestorPreviewFieldItem key={item.title} isMobile={isMobile}>
                                          <EditInvestorPreviewFieldTitle>{item.title}</EditInvestorPreviewFieldTitle>
                                          <EditInvestorPreviewFieldValue>{item.value}</EditInvestorPreviewFieldValue>
                                        </EditInvestorPreviewFieldItem>
                                      )
                                  )}
                                  {shouldShowChartWithTable(section, entityType) && drawdownTargets && (
                                    <DrawdownTargetsWrapper isMobile={isMobile}>
                                      <ChartWithTable drawdownTargetDialogData={drawdownTargets} />
                                    </DrawdownTargetsWrapper>
                                  )}
                                </EditInvestorPreviewFields>
                                {section.name === 'allocationTargets' && entityType === InvestorDetailsEntityTypeEnum.GroupedEntity && (
                                  <Banner
                                    icon={<InfoIcon fill={themeVar('menu.tab')} />}
                                    title=""
                                    description={`Refer to ${selectedGroupName} for allocation targets and related settings. Grouped entities do not have allocation targets of their own.`}
                                    color={themeVar('font.base')}
                                    bgColor={themeVar('layer.base')}
                                    dividerColor={themeVar('border.base')}
                                    withBorderRadius
                                  />
                                )}
                              </>
                            )}
                          </Accordion>
                        )
                    )}
                  </>
                )}
              </>
            )}
          </EditInvestorSectionsWrapper>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const SelectEntitySection = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '10px' : '20px')};
  padding: 4px 40px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;

const SelectEntitySectionButtons = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 10px;
  white-space: nowrap;
`;

const EntitySelectWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const EntityLabel = styled.div<{ isGroupedEntity?: boolean }>`
  font-size: 28px;
  font-weight: 700;
  line-height: 40px;
  padding: 10px 0;
  white-space: ${({ isGroupedEntity }) => (isGroupedEntity ? 'nowrap' : 'normal')};
  color: ${({ isGroupedEntity }) => (isGroupedEntity ? themeVar('font.action') : themeVar('font.strong'))};
`;

const EntitySelect = styled(Select)`
  color: ${themeVar('font.action')};
  font-size: 28px;
  font-weight: 700;
  line-height: 40px;
  background-color: transparent;
  border: none;
  gap: 10px;
  padding-left: 0;
  padding-right: 0;
  cursor: pointer;

  #dropdownSelect {
    font-size: 19px;
    line-height: 26.6px;
    font-weight: 400;
    min-width: 300px;
    left: -20px;
  }
`;

const AddNewEntityButton = styled(Button)`
  display: flex;
  align-self: flex-end;
  margin-left: auto;
`;

const DrawdownTargetsWrapper = styled.div<{ isMobile: boolean }>`
  padding: 40px 10px 10px 10px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '90%')};
`;
